import { getLogger } from '@logtape/logtape';
import { AxiosResponse } from 'axios';

import { getMockedResponse, mocked_cardreaders, sales_mocked_cardreaders } from '@/constants/mockedCardRead.ts';

import { api } from '@/lib/api-client.ts';

import {
    CardReadActionType,
    CardReadParams,
    CardReadResponseDto,
    CardReadResponseDtoWrapper,
    CardReadStatus,
    DCCardReader,
    FollowUpActionDto,
    GetCardReadersResponse,
    ReadCardRequestDto,
} from '@/models/doc-cirrus/DCCardReader.ts';

import { ServiceUrl } from '@/services/ServiceURL.ts';

import { isDevEnvironment, isSalesEnvironment } from '@/utils/envUtils.ts';
import { serializeApiError } from '@/utils/log-utils.ts';
import { getRandomEntry, waitRandom } from '@/utils/mockUtils.ts';

const logger = getLogger(['service', 'card-read']);

export const readCard = async (
    request_dto: ReadCardRequestDto,
    instance_id: string,
    customer_id: string
): Promise<CardReadResponseDto | undefined> => {
    if (isDevEnvironment() || isSalesEnvironment()) {
        await waitRandom();
        const response_status = getRandomEntry<CardReadStatus>([
            CardReadStatus.APPLIED,
            CardReadStatus.MERGED,
            CardReadStatus.MATCHING,
        ]);
        if (!response_status) {
            return undefined;
        }

        return getMockedResponse(response_status);
    }

    const response: AxiosResponse<CardReadResponseDtoWrapper> = await api.post(
        ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.READ_CARD,
        request_dto,
        {
            headers: {
                customer_id: customer_id,
            },
            params: {
                instance_id: instance_id,
            },
        }
    );

    return response.data?.data;
};

export const followUpAction = async (
    request_dto: FollowUpActionDto,
    instance_id: string,
    customer_id: string,
    pvs_user_id?: string
): Promise<CardReadResponseDto | undefined> => {
    if (isDevEnvironment() || isSalesEnvironment()) {
        switch (request_dto.action) {
            case CardReadActionType.MATCHING_SELECT: {
                await waitRandom();
                return getMockedResponse(CardReadStatus.APPLIED, pvs_user_id);
            }
            case CardReadActionType.CANCEL: {
                await waitRandom();
                return getMockedResponse(CardReadStatus.CANCELLED);
            }
            case CardReadActionType.APPLY: {
                await waitRandom();
                return getMockedResponse(CardReadStatus.APPLIED);
            }
            default: {
                return undefined;
            }
        }
    }

    const params: CardReadParams = {
        instance_id: instance_id,
    };
    if (pvs_user_id) {
        params.dc_user_id = pvs_user_id;
    }

    try {
        const response: AxiosResponse<CardReadResponseDtoWrapper> = await api.post(
            ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.READ_CARD,
            request_dto,
            {
                headers: {
                    customer_id: customer_id,
                },
                params: params,
            }
        );

        return response.data?.data;
    } catch (error) {
        logger.error('Failed to run the card-read follow-up action', { error: error, ...serializeApiError(error) });
        return undefined;
    }
};

export const fetchByInstance = async (
    instance_id: string,
    customer_id: string
): Promise<GetCardReadersResponse | undefined> => {
    try {
        const response: AxiosResponse<GetCardReadersResponse> = await api.get(
            ServiceUrl.PATIENT_API_DOMAIN.SECURE_ADMIN.DOC_CIRRUS.CARD_READERS,
            {
                headers: {
                    customer_id: customer_id,
                },
                params: {
                    instance_id: instance_id,
                },
            }
        );

        return response.data;
    } catch (error) {
        logger.error('Failed to fetch the card readers for the instance', {
            error: error,
            ...serializeApiError(error),
        });
        return undefined;
    }
};

export const getCardreadersForInstances = async (
    instance_ids: string[],
    customer_id: string
): Promise<GetCardReadersResponse> => {
    const is_sales_environment = isSalesEnvironment();
    if (isDevEnvironment() || is_sales_environment) {
        await waitRandom();
        return {
            card_readers: is_sales_environment ? sales_mocked_cardreaders : mocked_cardreaders,
        };
    }

    const promises = instance_ids.map((instance_id) => fetchByInstance(instance_id, customer_id));
    const responses: (GetCardReadersResponse | undefined)[] = await Promise.all(promises);
    const filtered_responses: GetCardReadersResponse[] = responses.filter((response) => !!response);
    let card_readers: DCCardReader[] = filtered_responses.flatMap((response) => response.card_readers);

    card_readers = card_readers.filter((entry) => !entry.workplace.name?.toLowerCase().includes('hosting'));
    return { card_readers };
};
